import { useEffect, useRef, useState } from 'react';

/**
 * A Reading paper being sat, in ten seconds, for the page that tells centres
 * what Target does.
 *
 * Motion with a job — explanation: a pointer answers two questions, hands
 * the paper in, and the band comes back. Drawn in the exam's own plain black
 * so it reads as the real screen, not an illustration of one.
 *
 * The whole of it is one CSS timeline, which runs off the main thread and
 * stays smooth while the page is loading. It plays only while it is on
 * screen and the tab is in front, and someone who has asked for less motion
 * sees the finished state, still: both answers chosen, the band back.
 *
 * Decoration to a screen reader, which is given one sentence instead.
 */
export function ExamDemo() {
  const box = useRef<HTMLDivElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [seconds, setSeconds] = useState(59 * 60 + 12);

  useEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    let onScreen = false;
    const update = () => setPlaying(onScreen && !document.hidden);
    const observer = new IntersectionObserver(([entry]) => {
      onScreen = Boolean(entry?.isIntersecting);
      update();
    });
    observer.observe(el);
    document.addEventListener('visibilitychange', update);
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', update);
    };
  }, []);

  // The clock counts down while the demo plays, as the real one does.
  useEffect(() => {
    if (!playing) return undefined;
    const tick = window.setInterval(() => setSeconds((s) => (s > 0 ? s - 1 : 59 * 60 + 12)), 1000);
    return () => window.clearInterval(tick);
  }, [playing]);

  const clock = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;

  return (
    <figure className="lp-shot lp-shot--hero">
      <div
        ref={box}
        className="xdemo"
        data-playing={playing || undefined}
        role="img"
        aria-label="A Reading paper being sat on Target: two questions answered, the paper handed in, and a band of 7.0 returned."
      >
        <div className="xdemo__bar">
          <span>
            <b>Aziza Karimova</b> 607496
          </span>
          <b className="xdemo__clock">{clock}</b>
          <span className="xdemo__menu" />
        </div>
        <div className="xdemo__part">
          <b>Part 1</b>
          <span>Questions 1–13.</span>
        </div>
        <div className="xdemo__panes">
          <div className="xdemo__passage">
            <b>The return of the urban tram</b>
            {[96, 88, 93, 70, 0, 94, 90, 85, 60, 0, 92, 95, 78].map((w, i) =>
              w ? <i key={i} style={{ width: `${w}%` }} /> : <br key={i} />,
            )}
          </div>
          <div className="xdemo__questions">
            <p className="xdemo__rubric">Do the following statements agree with the information?</p>
            <Question
              n={1}
              text="Horse trams ran for around fifty years before electricity."
              chosen="A"
              which="one"
            />
            <Question n={2} text="Most operators kept ten horses for each car." chosen="B" which="two" />
            <Question n={3} text="Steam trams were banned by national law." />
          </div>
        </div>
        <span className="xdemo__next" aria-hidden="true">
          →
        </span>
        <div className="xdemo__result">
          <span className="xdemo__resultLabel">Reading · marked</span>
          <b className="xdemo__band">7.0</b>
        </div>
        <svg className="xdemo__pointer" viewBox="0 0 16 22" aria-hidden="true">
          <path d="M1 1 L1 17 L5.5 13 L8.5 20 L11 19 L8 12 L14 12 Z" />
        </svg>
      </div>
    </figure>
  );
}

function Question({
  n,
  text,
  chosen,
  which,
}: {
  n: number;
  text: string;
  chosen?: 'A' | 'B';
  which?: 'one' | 'two';
}) {
  return (
    <div className="xdemo__q">
      <p>
        <span className="xdemo__n">{n}</span>
        {text}
      </p>
      {(['A', 'B', 'C'] as const).map((letter, i) => (
        <div
          key={letter}
          className={`xdemo__opt${chosen && letter === chosen ? ` xdemo__opt--pick xdemo__opt--${which}` : ''}`}
        >
          <span className="xdemo__letter">{letter}</span>
          <span className="xdemo__radio" />
          <span>{['TRUE', 'FALSE', 'NOT GIVEN'][i]}</span>
        </div>
      ))}
    </div>
  );
}
